import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Pagination,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import * as XLSX from "xlsx";

const AuditRequestsList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Build query params
  const [queryParams, setQueryParams] = useState("");

  useEffect(() => {
    let params = `page=${page}&page_size=10`;
    if (searchTerm) {
      params += `&search=${encodeURIComponent(searchTerm)}`;
    }
    if (methodFilter !== "all") {
      params += `&method=${methodFilter}`;
    }
    if (statusFilter !== "all") {
      params += `&status_code=${statusFilter}`;
    }
    if (userFilter !== "all") {
      params += `&username=${encodeURIComponent(userFilter)}`;
    }
    if (dateRange !== "all") {
      const now = new Date();
      let startDate = new Date();
      switch (dateRange) {
        case "today":
          startDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          startDate.setDate(now.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          break;
        case "month":
          startDate.setMonth(now.getMonth() - 1);
          startDate.setHours(0, 0, 0, 0);
          break;
        default:
          break;
      }
      params += `&started_at__gte=${startDate.toISOString()}`;
    }
    setQueryParams(params);
  }, [page, searchTerm, methodFilter, statusFilter, userFilter, dateRange]);

  const { data, isLoading, isError } = useFetchObjects(
    ["audit-requests", queryParams],
    `audit-requests/?${queryParams}`,
    token,
    { enabled: !!token }
  );

  // Get unique values for filters
  const { data: allRequests } = useFetchObjects(
    ["audit-requests-all"],
    "audit-requests/?page_size=1000",
    token,
    { enabled: !!token }
  );

  const allRequestsData = Array.isArray(allRequests)
    ? allRequests
    : allRequests?.results || [];
  const uniqueUsers = [
    ...new Set(
      allRequestsData.map((req) => req.username).filter(Boolean)
    ),
  ].sort();
  const uniqueMethods = [
    ...new Set(allRequestsData.map((req) => req.method).filter(Boolean)),
  ].sort();
  const uniqueStatuses = [
    ...new Set(
      allRequestsData.map((req) => req.status_code).filter(Boolean)
    ),
  ].sort();

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setOpenDialog(true);
  };

  const getStatusCodeColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "success";
    if (statusCode >= 300 && statusCode < 400) return "info";
    if (statusCode >= 400 && statusCode < 500) return "warning";
    if (statusCode >= 500) return "error";
    return "default";
  };

  const handleExport = (exportFormat) => {
    const requests = Array.isArray(data) ? data : data?.results || [];
    const exportData = requests.map((req) => ({
      Date: req.started_at
        ? format(parseISO(req.started_at), "yyyy-MM-dd HH:mm:ss")
        : "-",
      Method: req.method || "-",
      Path: req.path || "-",
      User: req.username || "-",
      "Status Code": req.status_code || "-",
      "IP Address": req.ip_address || "-",
      Duration: req.finished_at && req.started_at
        ? `${Math.round(
            (new Date(req.finished_at) - new Date(req.started_at)) / 1000
          )}s`
        : "-",
    }));

    if (exportFormat === "csv") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Audit Requests");
      XLSX.writeFile(wb, `audit-requests-${new Date().toISOString()}.csv`);
    } else if (exportFormat === "xlsx") {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Audit Requests");
      XLSX.writeFile(wb, `audit-requests-${new Date().toISOString()}.xlsx`);
    }
  };

  // Handle both paginated and non-paginated responses
  const requests = Array.isArray(data) ? data : data?.results || [];
  const totalCount = data?.count || requests.length;

  const clearFilters = () => {
    setMethodFilter("all");
    setStatusFilter("all");
    setUserFilter("all");
    setDateRange("all");
    setSearchTerm("");
    setPage(1);
  };

  const activeFiltersCount =
    (methodFilter !== "all" ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (userFilter !== "all" ? 1 : 0) +
    (dateRange !== "all" ? 1 : 0) +
    (searchTerm ? 1 : 0);

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Grid>
    );
  }

  if (isError) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{t("audit.errorLoadingRequests")}</Typography>
      </Grid>
    );
  }

  return (
    <Grid container maxWidth="xl" mb={3}>
      {/* Filters and Search */}
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t("audit.searchRequests")}
              variant="outlined"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("audit.method")}</InputLabel>
              <Select
                value={methodFilter}
                label={t("audit.method")}
                onChange={(e) => {
                  setMethodFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">{t("audit.all")}</MenuItem>
                {uniqueMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("audit.statusCode")}</InputLabel>
              <Select
                value={statusFilter}
                label={t("audit.statusCode")}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">{t("audit.all")}</MenuItem>
                {uniqueStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("audit.timeRange")}</InputLabel>
              <Select
                value={dateRange}
                label={t("audit.timeRange")}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">{t("audit.allTime")}</MenuItem>
                <MenuItem value="today">{t("audit.today")}</MenuItem>
                <MenuItem value="week">{t("audit.lastWeek")}</MenuItem>
                <MenuItem value="month">{t("audit.lastMonth")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title={t("audit.export")}>
                <IconButton
                  onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  color="primary"
                >
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
              {activeFiltersCount > 0 && (
                <Tooltip title={t("audit.clearFilters")}>
                  <IconButton onClick={clearFilters} color="secondary">
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
        </Grid>
        {activeFiltersCount > 0 && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={`${activeFiltersCount} ${t("audit.filtersActive")}`}
              onDelete={clearFilters}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Grid>

      {/* Export Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExport("csv")}>
          {t("audit.exportCSV")}
        </MenuItem>
        <MenuItem onClick={() => handleExport("xlsx")}>
          {t("audit.exportExcel")}
        </MenuItem>
      </Menu>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("audit.date")}</TableCell>
              <TableCell>{t("audit.method")}</TableCell>
              <TableCell>{t("audit.path")}</TableCell>
              <TableCell>{t("audit.user")}</TableCell>
              <TableCell>{t("audit.statusCode")}</TableCell>
              <TableCell>{t("audit.ipAddress")}</TableCell>
              <TableCell>{t("audit.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    {t("audit.noRequestsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    {request.started_at
                      ? format(parseISO(request.started_at), "yyyy-MM-dd HH:mm:ss")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip label={request.method} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: "200px" }}>
                      {request.path}
                    </Typography>
                  </TableCell>
                  <TableCell>{request.username || "-"}</TableCell>
                  <TableCell>
                    {request.status_code ? (
                      <Chip
                        label={request.status_code}
                        size="small"
                        color={getStatusCodeColor(request.status_code)}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{request.ip_address || "-"}</TableCell>
                  <TableCell>
                    <Tooltip title={t("audit.viewDetails")}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(request)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalCount > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(totalCount / 10)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("audit.requestDetails")}</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>{t("audit.startedAt")}:</strong>{" "}
                {selectedRequest.started_at
                  ? format(parseISO(selectedRequest.started_at), "yyyy-MM-dd HH:mm:ss")
                  : "-"}
              </Typography>
              {selectedRequest.finished_at && (
                <Typography variant="body2" gutterBottom>
                  <strong>{t("audit.finishedAt")}:</strong>{" "}
                  {format(parseISO(selectedRequest.finished_at), "yyyy-MM-dd HH:mm:ss")}
                </Typography>
              )}
              {selectedRequest.finished_at && selectedRequest.started_at && (
                <Typography variant="body2" gutterBottom>
                  <strong>{t("audit.duration")}:</strong>{" "}
                  {Math.round(
                    (new Date(selectedRequest.finished_at) -
                      new Date(selectedRequest.started_at)) /
                      1000
                  )}
                  s
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>{t("audit.method")}:</strong> {selectedRequest.method}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>{t("audit.path")}:</strong> {selectedRequest.path}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>{t("audit.user")}:</strong>{" "}
                {selectedRequest.username || "-"}
              </Typography>
              {selectedRequest.status_code && (
                <Typography variant="body2" gutterBottom>
                  <strong>{t("audit.statusCode")}:</strong>{" "}
                  {selectedRequest.status_code}
                </Typography>
              )}
              {selectedRequest.ip_address && (
                <Typography variant="body2" gutterBottom>
                  <strong>{t("audit.ipAddress")}:</strong>{" "}
                  {selectedRequest.ip_address}
                </Typography>
              )}
              {selectedRequest.user_agent && (
                <Typography variant="body2" gutterBottom>
                  <strong>{t("audit.userAgent")}:</strong>{" "}
                  {selectedRequest.user_agent}
                </Typography>
              )}
              {selectedRequest.referrer && (
                <Typography variant="body2" gutterBottom>
                  <strong>{t("audit.referrer")}:</strong>{" "}
                  {selectedRequest.referrer}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>{t("audit.requestId")}:</strong>{" "}
                <code style={{ fontSize: "11px" }}>
                  {selectedRequest.request_id}
                </code>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t("audit.close")}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default AuditRequestsList;
